import { database } from '@app/firebase';
import asyncRender from '@components/async/asyncRender';
import CalendarAddDialog from '@components/dialogs/CalendarAddDialog';
import Error from '@components/error/Error';
import { RouterLink } from '@components/link-behavior/LinkBehavior';
import NotificationContext from '@context/NotificationContext';
import UserContext from '@context/UserContext';
import useCollection from '@hooks/useCollection';
import Sentry from '@integrations/Sentry';
import IconPlus from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ROUTES from '@routes';
import useCommonStyles from '@styles/common.style';
import newId from '@utils/newId';
import { collection as firestoreCollection, doc, query as firestoreQuery, setDoc, Timestamp } from 'firebase/firestore';
import React, { useContext } from 'react';
import { generatePath } from 'react-router';

import { DefaultFirestoreConverter } from '../../../types/DefaultFirestoreConverter';
import CalendarsSkeleton from './Calendars.skeleton';
import CalendarsActions from './CalendarsActions';

const Calendars = () => {
  const { classes: common, theme } = useCommonStyles();
  const userContext = useContext<UserContext>(UserContext);
  const notificationContext = useContext<NotificationContext>(NotificationContext);

  const query = firestoreQuery(firestoreCollection(database, 'calendars').withConverter(new DefaultFirestoreConverter<guesthouse.Calendar>()));

  const { collection, loading, error } = useCollection<guesthouse.Calendar>(query);

  if (error) {
    return (
      <div className={common.contentSpacing}>
        <Error />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={common.contentSpacing}>
        <CalendarsSkeleton />
      </div>
    );
  }

  const docs = collection && collection.docs;

  let title = '0 Calendars';

  if (docs && docs.length) {
    title = `${docs.length} Calendar${docs.length > 1 ? 's' : ''}`;
  }

  return (
    <div className={common.contentSpacing}>
      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          style={{ marginBottom: theme.spacing(3) }}
        >
          <Box
            display="flex"
            alignItems="center"
          >
            <Typography
              component="h1"
              variant="h3Alt"
              style={{ marginRight: theme.spacing(1) }}
            >
              {title}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              style={{ marginLeft: theme.spacing(2) }}
              startIcon={<IconPlus />}
              onClick={() => {
                asyncRender(CalendarAddDialog, { userContext })
                  .then((result: guesthouse.Calendar) => {
                    if (result) {
                      const docID = newId();
                      const calendar: guesthouse.Calendar = {
                        ...result,
                        docID,
                        created: Timestamp.fromDate(new Date()),
                      };

                      return setDoc(
                        doc(firestoreCollection(database, 'calendars'), docID),
                        calendar
                      );
                    }
                  })
                  .catch((e) => {
                    if (e) {
                      Sentry.captureException(e);
                      notificationContext.setContext({ open: true, message: e.message, severity: 'error' });
                    }
                  });
              }}
            >
              Add Calendar
            </Button>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >

        </Grid>
      </Grid>

      {docs?.length
        ? (
          <TableContainer>


            <Table aria-label="calendars table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    Title
                  </TableCell>
                  <TableCell>
                    Google Calendar ID
                  </TableCell>
                  <TableCell align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.map((doc) => {
                  const calendar = doc.data() as guesthouse.Calendar;

                  return (
                    <TableRow key={calendar.title}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={generatePath(ROUTES.calendar.path, { id: calendar.docID })}
                        >
                          {calendar.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {calendar.googleCalendarId}
                      </TableCell>
                      <TableCell
                        width="100"
                        height="60"
                        align="center"
                      >
                        <CalendarsActions calendar={doc} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
        : (
          <Typography>
            There&apos;s nothing here yet.
          </Typography>
        )}
    </div>
  );
};

export default React.memo(Calendars);
